import axios from 'axios'
import { Capacitor } from '@capacitor/core'

// key ที่ใช้เก็บ backend URL override ที่ตั้งผ่านหน้า Settings (PIN-protected) บนแอปมือถือ
// อ่านที่นี่ที่เดียว ไม่มีที่อื่นในระบบควรอ่าน key นี้ตรงๆ
export const API_URL_OVERRIDE_KEY = 'assetos_api_url_override'

// ── key แยกต่างหากสำหรับ "tenant ไหน" — คนละเรื่องกับ API_URL_OVERRIDE_KEY ข้างบนโดยสิ้นเชิง:
// API_URL_OVERRIDE_KEY บอกว่า "จะคุยกับ backend ตัวไหน" (จะแยก backend ต่อ tenant หรือใช้ตัว
// เดียวกันทั้งหมดก็ได้ แล้วแต่วิธี deploy) ส่วน key นี้บอกว่า "กำลังคุยแทน tenant ไหน" ผ่าน header
// X-Tenant-Host — จำเป็นเฉพาะ native app เท่านั้น เพราะ Capacitor WebView ไม่มี hostname จริง
// แบบเว็บเบราว์เซอร์ (window.location.hostname จะได้ค่าเช่น "localhost" หรือ scheme ของ
// Capacitor เอง ไม่ใช่โดเมนของ tenant จริงเลย) — เว็บ/PWA ไม่ต้องใช้ key นี้เลย ได้ hostname
// จริงจาก browser อยู่แล้วโดยอัตโนมัติ ──
export const TENANT_HOST_OVERRIDE_KEY = 'assetos_tenant_host_override'

/** คืน hostname ที่จะส่งเป็น X-Tenant-Host — native app ใช้ค่าที่ตั้งไว้ผ่าน Settings ก่อนเสมอ
 *  (ถ้ามี) ไม่งั้น fallback ไป window.location.hostname เหมือนเดิมทุกประการ (ครอบคลุมเว็บ/PWA
 *  และกรณี native ที่ยังไม่เคยตั้งค่า tenant เอง) */
export function getTenantHost(): string {
  if (Capacitor.isNativePlatform()) {
    const override = localStorage.getItem(TENANT_HOST_OVERRIDE_KEY)
    if (override) return override
  }
  return window.location.hostname
}

// ── Single source of truth for the API base URL ──────────────────────────────
// ทุกไฟล์ในระบบต้อง import API_BASE_URL จากที่นี่ที่เดียว ห้ามประกาศ fallback URL
// ของตัวเองแยกต่างหากอีก (เคยมีปัญหานี้มาแล้ว — ไฟล์อื่นๆ hardcode URL ของ
// deployment เก่าไว้เป็น fallback ทำให้พอย้ายไป tenant ใหม่แล้วลืมตั้ง
// VITE_API_URL ให้ครบทุกที่ ระบบจะแอบยิง request ไปหา backend ของ deployment
// เก่า/ของลูกค้าอื่นแทน โดยไม่มี error ให้เห็นเลย)
function getApiBaseUrl(): string {
  // Native app เท่านั้น — ให้ตั้งค่าเปลี่ยน backend ตอน runtime ได้ผ่านหน้า Settings
  // (PIN-protected) ที่หน้า Login เพื่อให้ APK เดียวใช้ได้กับหลาย Tenant โดยไม่ต้อง
  // build ใหม่ทุกครั้ง — เว็บ/PWA ไม่ใช้ path นี้เลย ยังคงพึ่ง VITE_API_URL เท่านั้น
  if (Capacitor.isNativePlatform()) {
    const override = localStorage.getItem(API_URL_OVERRIDE_KEY)
    if (override) return override
  }

  const configured = import.meta.env.VITE_API_URL
  if (configured) return configured

  // Dev fallback เท่านั้น (npm run dev แบบไม่ได้ตั้ง env) — ห้ามมี fallback
  // เป็น URL จริงของ deployment ไหนทั้งนั้น เพราะโค้ดชุดนี้ต้อง deploy ซ้ำให้
  // ลูกค้าหลายรายที่แยกโปรเจกต์/แยก DB กันคนละชุด
  if (import.meta.env.DEV) return 'http://localhost:3000'

  // Production build ที่ไม่ได้ตั้ง VITE_API_URL — ต้องพังให้เห็นชัดๆ ทันที
  // ดีกว่าไปยิง request หา backend ผิดเจ้าของแบบเงียบๆ
  console.error(
    '[Config] VITE_API_URL ไม่ได้ถูกตั้งค่า — ตรวจสอบ Environment Variable ' +
    'ของ frontend service (ต้องมี https:// นำหน้า และตั้งเป็น ARG ใน Dockerfile ด้วย)'
  )
  return ''
}

export const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  // ── ส่ง hostname ของ tenant ไปด้วยเสมอ — จำเป็นตอน frontend/backend คนละโดเมนกัน (เช่น
  // Railway ที่ frontend เรียก api.assetsservice.com ตรงๆ แบบ cross-origin) เพราะ backend เอง
  // จะเห็นแค่โดเมนของตัวเองใน Host header เสมอ ไม่รู้ว่ากำลังคุยแทน tenant ไหน — สำหรับเว็บ/PWA
  // ใช้ window.location.hostname ตรงๆ ได้เลย (เห็น URL จริงของ browser) แต่ native app ไม่มี
  // "URL จริง" แบบนี้เลย (Capacitor WebView) เลยต้องพึ่งค่าที่ตั้งไว้ผ่านหน้า Settings แทน —
  // ดู getTenantHost() ด้านบนและ tenant-subdomain.middleware.ts ฝั่ง backend ──
  config.headers['X-Tenant-Host'] = getTenantHost()
  return config
})

let isRedirecting = false

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !isRedirecting) {
      // ตรวจสอบว่าไม่ใช่ request จาก login page
      const isLoginRequest = err.config?.url?.includes('/auth/login')
      if (!isLoginRequest) {
        isRedirecting = true
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
    }
    return Promise.reject(err)
  }
)

export default api

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login = (username: string, password: string) =>
  api.post('/auth/login', { username, password })
export const logout = () => api.post('/auth/logout')

// ── Assets ────────────────────────────────────────────────────────────────────
export const getAssets = (status?: string) =>
  api.get('/assets', { params: status ? { status } : {} })
export const getAllAssetsAdmin = () => api.get('/assets/all')
export const getRepairAssets = () => api.get('/assets/repair-all')
export const getAsset = (id: number) => api.get(`/assets/${id}`)
export const getAssetsPaginated = (params: {
  page: number; limit: number; status?: string; departmentCode?: string; category?: string; subCategory?: string; location?: string; search?: string
  sortBy?: string; sortDir?: 'ASC' | 'DESC'
}) => api.get('/assets/paginated', { params })
export const checkAssetDuplicate = (params: {
  assetCode?: string; serialNumber?: string; excludeId?: number
}) => api.get('/assets/check', { params })
export const scanLookupAsset = (q: string) => api.get('/assets/scan-lookup', { params: { q } })
export const createAsset = (data: any) => api.post('/assets', data)
export const updateAsset = (id: number, data: any) => api.put(`/assets/${id}`, data)
export const updateBorrow    = (id: number, data: any) => api.patch(`/borrows/${id}/update`, data)
export const updateTransfer  = (id: number, data: any) => api.patch(`/transfers/${id}/update`, data)
export const deleteAsset     = (id: number) => api.delete(`/assets/${id}`)
export const hardDeleteAsset = (id: number) => api.delete(`/assets/${id}/hard`)
export const reportAssetLost    = (id: number, body: any) => api.post(`/assets/${id}/lost`, body)
export const markAssetInstalled = (id: number, body: { location: string; installedDate?: string; note?: string }) => api.post(`/assets/${id}/install`, body)
export const uninstallAsset     = (id: number, body?: { note?: string }) => api.post(`/assets/${id}/uninstall`, body ?? {})
export const markAssetFound     = (id: number, body: any) => api.post(`/assets/${id}/found`, body)
export const reportAssetDamaged = (id: number, body: any) => api.post(`/assets/${id}/damaged`, body)
export const updateDamagedInfo  = (id: number, body: any) => api.put(`/assets/${id}/damaged`, body)
export const uploadLostIncident    = (id: number, file: File) => {
  const form = new FormData(); form.append('file', file)
  return api.post(`/assets/${id}/lost/incident`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const uploadDamagedIncident = (id: number, file: File) => {
  const form = new FormData(); form.append('file', file)
  return api.post(`/assets/${id}/damaged/incident`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const getImportReference = () => api.get('/assets/import-reference')
export const importAssets = (file: File) => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/assets/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const uploadInventoryImage = (id: number, file: File) => {
  const form = new FormData(); form.append('image', file)
  return api.post(`/inventory/items/${id}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const uploadAssetImage = (id: number, file: File) => {
  const form = new FormData()
  form.append('image', file)
  return api.post(`/assets/${id}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const getNextAssetCode = (dept: string, category: string) =>
  api.get('/assets/next-code', { params: { dept, category } })

// ── Borrows ───────────────────────────────────────────────────────────────────
export const getBorrows = () => api.get('/borrows')
export const borrowAsset = (assetId: number, department: string) =>
  api.post('/borrows/borrow', { assetId, department })
export const returnAsset = (borrowId: number) => api.post(`/borrows/${borrowId}/return`)
export const requestBorrow = (data: any) => api.post('/borrows/request', data)
export const approveBorrow = (id: number, data?: any) => api.patch(`/borrows/${id}/approve`, data ?? {})
export const rejectBorrow = (id: number, reason?: string) => api.patch(`/borrows/${id}/reject`, { reason })
export const partialReturnBorrow = (id: number, assetIds: number[], note?: string) =>
  api.post(`/borrows/${id}/partial-return`, { assetIds, note })
export const getPendingBorrows = () => api.get('/borrows/pending')
export const getMyBorrows = () => api.get('/borrows/my')
export const getAllBorrows = (status?: string) =>
  api.get('/borrows', { params: status ? { status } : {} })

// ── Maintenance ───────────────────────────────────────────────────────────────
export const getMaintenances = () => api.get('/maintenance')
export const createMaintenance = (data: any) => api.post('/maintenance', data)
export const completeMaintenance = (id: number, data: any) =>
  api.patch(`/maintenance/${id}/complete`, data)
export const getMaintenanceDue = (days = 7) =>
  api.get('/maintenance/due', { params: { days } })

// ── Reports ───────────────────────────────────────────────────────────────────
export const getDashboard      = ()           => api.get('/reports/dashboard')
export const getRecentActivity = (limit = 20) => api.get(`/assets/recent-activity?limit=${limit}`)
export const getDepreciationReport = (method = 'STRAIGHT_LINE', usefulLife = 5) =>
  api.get('/reports/depreciation', { params: { method, usefulLife } })

// ── Handover ──────────────────────────────────────────────────────────────────
export const createHandover = (data: any) => api.post('/handovers', data)
export const updateHandover = (id: number, data: any) => api.put(`/handovers/${id}`, data)
export const receiveHandover = (id: number, signature: string) =>
  api.patch(`/handovers/${id}/receive`, { signature })
export const resendHandoverConfirmEmail = (id: number, email?: string) =>
  api.post(`/handovers/${id}/resend-confirm-email`, { email })
export const getPublicHandover = (token: string) => api.get(`/handover-public/${token}`)
export const confirmPublicHandover = (token: string, signature: string) =>
  api.post(`/handover-public/${token}/confirm`, { signature })
export const rejectPublicHandover = (token: string, reason: string) =>
  api.post(`/handover-public/${token}/reject`, { reason })
export const downloadHandoverPdf = (id: number, lang?: 'th' | 'en') =>
  api.get(`/handovers/${id}/pdf`, { responseType: 'blob', params: lang ? { lang } : undefined })
export const downloadTransferPdf = (id: number, lang?: 'th' | 'en') =>
  api.get(`/transfers/${id}/pdf`, { responseType: 'blob', params: lang ? { lang } : undefined })
export const downloadBorrowPdf = (id: number, lang?: 'th' | 'en') =>
  api.get(`/borrows/${id}/pdf`, { responseType: 'blob', params: lang ? { lang } : undefined })
export const downloadLicensePdf = (ids: number[], lang?: 'th' | 'en') =>
  api.get(`/licenses/pdf`, { responseType: 'blob', params: { ids: ids.join(','), ...(lang ? { lang } : {}) } })
export const requestBorrowExtend = (id: number, newDueDate: string, reason?: string) =>
  api.post(`/borrows/${id}/extend-request`, { newDueDate, reason })
export const approveBorrowExtend = (id: number) =>
  api.patch(`/borrows/${id}/extend-approve`)
export const rejectBorrowExtend = (id: number, reason?: string) =>
  api.patch(`/borrows/${id}/extend-reject`, { reason })
export const getHandoverHistory = (id: number) =>
  api.get(`/handovers/${id}/history`)
export const getTransferHistory = (id: number) =>
  api.get(`/transfers/${id}/history`)
export const getBorrowHistory = (id: number) =>
  api.get(`/borrows/${id}/history`)
export const updateHandoverAccessories = (id: number, data: { accessories?: any; note?: string; replacements?: { oldAssetId: number; reason?: string }[] }) =>
  api.patch(`/handovers/${id}/accessories`, data)
export const returnHandover = (
  id: number,
  data: { returnNote?: string; returnSignature?: string },
) => api.patch(`/handovers/${id}/return`, data)
export const cancelHandover = (id: number) => api.patch(`/handovers/${id}/cancel`)
export const approveHandover = (id: number) => api.patch(`/handovers/${id}/approve`)
export const rejectHandover = (id: number, reason?: string) => api.patch(`/handovers/${id}/reject`, { reason })
export const getHandovers = () => api.get('/handovers')
export const getHandover = (id: number) => api.get(`/handovers/${id}`)
export const getHandoversByAsset = (assetId: number) => api.get(`/handovers/asset/${assetId}`)
export const addReceiverSignature = (id: number, signature: string) =>
  api.patch(`/handovers/${id}/receiver-signature`, { signature })

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers = () => api.get('/users')
export const getUserSignatureHistory = (id: number) => api.get(`/users/${id}/signature-history`)
export const getUser = (id: number) => api.get(`/users/${id}`)
export const createUser = (data: any) => api.post('/users', data)
export const updateUser = (id: number, data: any) => api.put(`/users/${id}`, data)
export const deleteUser = (id: number) => api.delete(`/users/${id}`)
export const reactivateUser = (id: number) => api.patch(`/users/${id}/reactivate`)
export const getMyProfile = () => api.get('/users/me/profile')
export const updateMyProfile = (data: any) => api.patch('/users/me/profile', data)
export const getMyLineLinkStatus = () => api.get('/users/me/line-link')
export const unlinkMyLine = () => api.delete('/users/me/line-link')
export const adminUnlinkUserLine = (userId: number) => api.delete(`/users/${userId}/line-link`)

// ── Configuration ─────────────────────────────────────────────────────────────
export const getDepartments = () => api.get('/configuration/departments')
export const createDepartment = (data: any) => api.post('/configuration/departments', data)
export const updateDepartment = (id: number, data: any) =>
  api.put(`/configuration/departments/${id}`, data)
export const deleteDepartment = (id: number) => api.delete(`/configuration/departments/${id}`)
export const deactivateDepartment = (id: number) =>
  api.patch(`/configuration/departments/${id}/deactivate`)

export const getCategories = () => api.get('/configuration/categories')
export const createCategory = (data: any) => api.post('/configuration/categories', data)
export const updateCategory = (id: number, data: any) =>
  api.put(`/configuration/categories/${id}`, data)
export const deleteCategory = (id: number) => api.delete(`/configuration/categories/${id}`)
export const deactivateCategory = (id: number) =>
  api.patch(`/configuration/categories/${id}/deactivate`)

export const getSubCategories = (category?: string) =>
  api.get('/configuration/sub-categories', { params: category ? { category } : {} })
export const createSubCategory = (data: any) => api.post('/configuration/sub-categories', data)
export const updateSubCategory = (id: number, data: any) =>
  api.put(`/configuration/sub-categories/${id}`, data)
export const deleteSubCategory = (id: number) => api.delete(`/configuration/sub-categories/${id}`)
export const deactivateSubCategory = (id: number) =>
  api.patch(`/configuration/sub-categories/${id}/deactivate`)

// ── Audit Log ─────────────────────────────────────────────────────────────────
export const getAuditLogs = (params?: any) => api.get('/audit-logs', { params })

// ── Locations ─────────────────────────────────────────────────────────────────
// ── Work Order Category Config ───────────────────────────────────────────────
export const getWoCategories  = () => api.get('/configuration/wo-categories')
export const createWoCategory = (dto: any) => api.post('/configuration/wo-categories', dto)
export const updateWoCategory = (id: number, dto: any) => api.patch(`/configuration/wo-categories/${id}`, dto)
export const deleteWoCategory = (id: number) => api.delete(`/configuration/wo-categories/${id}`)

export const getLocations = (departmentCode?: string) =>
  api.get('/configuration/locations', { params: departmentCode ? { departmentCode } : {} })
export const createLocation = (data: any) => api.post('/configuration/locations', data)
export const updateLocation = (id: number, data: any) => api.put(`/configuration/locations/${id}`, data)
export const deleteLocation = (id: number) => api.delete(`/configuration/locations/${id}`)
export const deactivateLocation = (id: number) => api.patch(`/configuration/locations/${id}/deactivate`)

// ── Department Transfer ───────────────────────────────────────────────────────
export const transferDepartment = (data: any) => api.post('/configuration/transfer-dept', data)

// ── Location Move / Swap ──────────────────────────────────────────────────────
export const moveLocation = (data: any) => api.post('/configuration/move-location', data)
export const swapLocation = (data: any) => api.post('/configuration/swap-location', data)
export const swapAssets   = (assetIdA: number, assetIdB: number) => api.post('/assets/swap', { assetIdA, assetIdB })

// ── Asset History ─────────────────────────────────────────────────────────────
export const getAssetHistory = (assetId: number) => api.get(`/configuration/asset-history/${assetId}`)

// ── Positions ─────────────────────────────────────────────────────────────────
export const getPositions = () => api.get('/configuration/positions')
export const createPosition = (data: any) => api.post('/configuration/positions', data)
export const updatePosition = (id: number, data: any) => api.put(`/configuration/positions/${id}`, data)
export const deletePosition = (id: number) => api.delete(`/configuration/positions/${id}`)
export const deactivatePosition = (id: number) => api.patch(`/configuration/positions/${id}/deactivate`)

// ── Recipients ────────────────────────────────────────────────────────────────
export const getRecipients = () => api.get('/configuration/recipients')
export const createRecipient = (data: any) => api.post('/configuration/recipients', data)
export const updateRecipient = (id: number, data: any) => api.put(`/configuration/recipients/${id}`, data)
export const deleteRecipient = (id: number) => api.delete(`/configuration/recipients/${id}`)
export const deactivateRecipient = (id: number) => api.patch(`/configuration/recipients/${id}/deactivate`)

// ── Brands ────────────────────────────────────────────────────────────────────
export const getBrands = () => api.get('/configuration/brands')
export const createBrand = (data: any) => api.post('/configuration/brands', data)
export const updateBrand = (id: number, data: any) => api.put(`/configuration/brands/${id}`, data)
export const deleteBrand = (id: number) => api.delete(`/configuration/brands/${id}`)
export const deactivateBrand = (id: number) => api.patch(`/configuration/brands/${id}/deactivate`)

// ── Equipment Models ──────────────────────────────────────────────────────────
export const getModels = (brandCode?: string) =>
  api.get('/configuration/models', { params: brandCode ? { brandCode } : {} })
export const createModel = (data: any) => api.post('/configuration/models', data)
export const updateModel = (id: number, data: any) => api.put(`/configuration/models/${id}`, data)
export const deleteModel = (id: number) => api.delete(`/configuration/models/${id}`)
export const deactivateModel = (id: number) => api.patch(`/configuration/models/${id}/deactivate`)
export const uploadModelImage = (id: number, file: File) => {
  const form = new FormData(); form.append('file', file)
  return api.post(`/configuration/models/${id}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const deleteModelImage = (id: number) => api.delete(`/configuration/models/${id}/image`)
export const uploadLicenseLogo = (id: number, file: File) => {
  const form = new FormData(); form.append('file', file)
  return api.post(`/licenses/${id}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const deleteLicenseLogo = (id: number) => api.delete(`/licenses/${id}/logo`)

// ── Vendors ───────────────────────────────────────────────────────────────────
export const getVendors = () => api.get('/configuration/vendors')
export const createVendor = (data: any) => api.post('/configuration/vendors', data)
export const updateVendor = (id: number, data: any) => api.put(`/configuration/vendors/${id}`, data)
export const deleteVendor = (id: number) => api.delete(`/configuration/vendors/${id}`)
export const deactivateVendor = (id: number) => api.patch(`/configuration/vendors/${id}/deactivate`)

// ── Inventory / Consumables ───────────────────────────────────────────────────
export const getInventorySummary   = ()           => api.get('/inventory/summary')
export const getInventoryItems     = (lowStock?: boolean) => api.get('/inventory/items', { params: lowStock ? { lowStock: true } : {} })
export const getNextInventorySku   = ()           => api.get('/inventory/items/next-sku')
export const getInventoryItem      = (id: number) => api.get(`/inventory/items/${id}`)
export const getItemMovements      = (id: number) => api.get(`/inventory/items/${id}/movements`)
export const getRecentMovements    = ()           => api.get('/inventory/movements?limit=100')
export const getLowStockItems      = ()           => api.get('/inventory/items/low-stock')
export const createInventoryItem   = (data: any)  => api.post('/inventory/items', data)
export const updateInventoryItem   = (id: number, data: any) => api.put(`/inventory/items/${id}`, data)
export const deactivateInventoryItem = (id: number) => api.patch(`/inventory/items/${id}/deactivate`)
export const receiveStock          = (id: number, data: any) => api.post(`/inventory/items/${id}/receive`, data)
export const receiveStockBatch     = (data: any) => api.post(`/inventory/items/receive-batch`, data)
export const issueStock            = (id: number, data: any) => api.post(`/inventory/items/${id}/issue`, data)
export const adjustStock           = (id: number, data: any) => api.post(`/inventory/items/${id}/adjust`, data)
export const returnStockItem       = (id: number, data: any) => api.post(`/inventory/items/${id}/return`, data)
export const transferStock         = (id: number, data: any) => api.post(`/inventory/items/${id}/transfer`, data)
export const getItemStockByLocation = (id: number) => api.get(`/inventory/items/${id}/stock-by-location`)
export const getStockLocations     = () => api.get('/inventory/locations')
export const createStockLocation   = (data: any) => api.post('/inventory/locations', data)
export const updateStockLocation   = (id: number, data: any) => api.put(`/inventory/locations/${id}`, data)
export const getStockDailyUsage    = (days = 30) => api.get(`/inventory/dashboard/daily-usage?days=${days}`)
export const getStockMonthlyUsage  = (months = 12) => api.get(`/inventory/dashboard/monthly-usage?months=${months}`)
export const getStockCostByWO      = (days = 30) => api.get(`/inventory/dashboard/cost-by-work-order?days=${days}`)
export const getStockCostByHK      = (days = 30) => api.get(`/inventory/dashboard/cost-by-hk-order?days=${days}`)
// Phase 4 — Stock Reports
export const getStockCard          = (itemId: number, dateFrom?: string, dateTo?: string) =>
  api.get(`/inventory/reports/stock-card/${itemId}`, { params: { dateFrom, dateTo } })
export const getStockBalance       = (dateFrom?: string, dateTo?: string) =>
  api.get('/inventory/reports/stock-balance', { params: { dateFrom, dateTo } })
export const getStockMovementReport = (dateFrom?: string, dateTo?: string, kind?: string) =>
  api.get('/inventory/reports/stock-movement', { params: { dateFrom, dateTo, kind } })
export const getConsumptionReport  = (dateFrom?: string, dateTo?: string) =>
  api.get('/inventory/reports/consumption', { params: { dateFrom, dateTo } })
export const getAdjustmentReport   = (dateFrom?: string, dateTo?: string) =>
  api.get('/inventory/reports/adjustment', { params: { dateFrom, dateTo } })

// ── Notifications ─────────────────────────────────────────────────────────────
export const getNotifications         = ()           => api.get('/notifications')
export const markNotificationRead     = (id: number) => api.patch(`/notifications/${id}/read`)
export const markAllNotificationsRead = ()           => api.patch('/notifications/read-all')

// ── Audit Sessions ────────────────────────────────────────────────────────────
export const getAuditSessions     = ()                      => api.get('/audit-sessions')
export const getAuditSession      = (id: number)            => api.get(`/audit-sessions/${id}`)
export const getAuditReport       = (id: number)          => api.get(`/audit-sessions/${id}/report`)
export const createAuditSession   = (data: any)           => api.post('/audit-sessions', data)
export const scanAuditAsset       = (id: number, data: any) => api.post(`/audit-sessions/${id}/scan`, data)
export const closeAuditSession    = (id: number)          => api.patch(`/audit-sessions/${id}/close`)
export const lookupAuditAsset     = (assetCode: string)   => api.get('/audit-sessions/lookup', { params: { assetCode } })

// ── Licenses ──────────────────────────────────────────────────────────────────
export const getLicenseSummary    = ()                    => api.get('/licenses/summary')
export const getLicenses          = ()                    => api.get('/licenses')
export const getLicenseHistory    = (id: number)           => api.get(`/licenses/${id}/history`)
export const getLicense           = (id: number)          => api.get(`/licenses/${id}`)
export const getLicenseDependencyMap = ()                 => api.get('/licenses/dependency-map')
export const createLicense        = (data: any)           => api.post('/licenses', data)
export const updateLicense        = (id: number, data: any) => api.put(`/licenses/${id}`, data)
export const deactivateLicense    = (id: number)          => api.patch(`/licenses/${id}/deactivate`)
export const reactivateLicense    = (id: number)          => api.patch(`/licenses/${id}/reactivate`)
export const assignLicense        = (id: number, data: any) => api.post(`/licenses/${id}/assign`, data)
export const unassignLicense      = (assignmentId: number) => api.delete(`/licenses/assignments/${assignmentId}`)
export const renewLicense         = (id: number, data: any) => api.post(`/licenses/${id}/renew`, data)
export const bulkDeleteLicenses   = (ids: number[]) => api.delete('/licenses/bulk', { data: { ids } })
export const deleteLicense        = (id: number)    => api.delete(`/licenses/${id}`)
export const uploadLicenseAttachment = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file)
  return api.post(`/licenses/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const deleteLicenseAttachment = (id: number, url: string) => api.delete(`/licenses/${id}/attachments`, { data: { url } })
export const uploadRenewalAttachment = (renewalId: number, file: File) => {
  const fd = new FormData(); fd.append('file', file)
  return api.post(`/licenses/renewals/${renewalId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const deleteRenewalAttachment = (renewalId: number, url: string) => api.delete(`/licenses/renewals/${renewalId}/attachments`, { data: { url } })
export const updateRenewal = (renewalId: number, data: any) => api.put(`/licenses/renewals/${renewalId}`, data)
export const deleteRenewal = (renewalId: number) => api.delete(`/licenses/renewals/${renewalId}`)

// ── Reports ───────────────────────────────────────────────────────────────────
export const getAssetListReport       = ()     => api.get('/reports/assets')
export const getMaintenanceReport     = ()     => api.get('/reports/maintenance')
export const getBorrowReport          = ()     => api.get('/reports/borrows')

// ── Email ────────────────────────────────────────────────────────────────────
export const sendTestEmail    = (to: string) => api.post('/email/test', { to })
export const getEmailConfig   = () => api.get('/email/config')
export const verifyEmailConn  = () => api.get('/email/verify')
// (SuperAdmin LINE management + Platform tenant/DB-provision API functions ตัดออกแล้ว
//  พร้อมกับหน้า Super Admin panel — ดูคอมเมนต์ใน backend/src/tenancy/tenancy.module.ts)

// Public — ไม่ต้อง auth
export const getPublicCompanyInfo = (code: string) =>
  api.get(`/public/company/info?code=${encodeURIComponent(code)}`)
export const getAccessLevels    = () => api.get('/access-levels')
export const createAccessLevel  = (data: { name: string; description?: string; allowedPages: string[] }) => api.post('/access-levels', data)
export const updateAccessLevel  = (id: number, data: { name?: string; description?: string; allowedPages?: string[] }) => api.put(`/access-levels/${id}`, data)
export const deleteAccessLevel  = (id: number) => api.delete(`/access-levels/${id}`)
export const getReminderSchedules  = () => api.get('/company/line/reminders')
export const updateReminderSchedule = (orderType: string, data: { timeSlots: string[]; staleAfterHours: number; enabled: boolean }) =>
  api.put(`/company/line/reminders/${orderType}`, data)

export const getLicensesByAsset = (assetCode: string) => api.get(`/licenses/by-asset/${assetCode}`)
export const getAllAssetLicenseMap = () => api.get('/licenses/asset-assignments')

// ── Repairs ───────────────────────────────────────────────────────────────────
export const getRepairJobs       = ()              => api.get('/repairs')
export const updateRepairJob     = (id: number, data: any) => api.patch(`/repairs/${id}`, data)
export const swapWithSpare       = (id: number, data: any) => api.patch(`/repairs/${id}/swap`, data)
export const getRepairJob        = (id: number)    => api.get(`/repairs/${id}`)
export const getRepairsByAsset   = (code: string)  => api.get(`/repairs/by-asset/${code}`)
export const getRepairImagesByAsset = (assetId: number) => api.get(`/repairs/images-by-asset/${assetId}`)
export const createRepairJob     = (data: any)     => api.post('/repairs', data)
export const completeRepair      = (id: number, data: any) => api.patch(`/repairs/${id}/complete`, data)
export const downloadRepairPdf   = (id: number, doc: 'outward' | 'inward' | 'internal', department?: string, lang?: 'th' | 'en') =>
  api.get(`/repairs/${id}/pdf`, { responseType: 'blob', params: { doc, ...(department ? { department } : {}), ...(lang ? { lang } : {}) } })
export const replaceAsset        = (id: number, data: any) => api.patch(`/repairs/${id}/replace`, data)
export const writeOffRepair      = (id: number, data: any) => api.patch(`/repairs/${id}/write-off`, data)
export const uploadRepairImages  = (id: number, files: FormData) => api.post(`/repairs/${id}/images`, files, { headers: { 'Content-Type': 'multipart/form-data' } })
export const deleteRepairImage   = (id: number, url: string) => api.delete(`/repairs/${id}/images`, { params: { url } })
export const uploadRepairVideo   = (id: number, file: File) => {
  const fd = new FormData(); fd.append('file', file)
  return api.post(`/repairs/${id}/video`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// ── Transfers ──────────────────────────────────────────────────────────────
export const getTransfers      = () => api.get('/transfers')
export const createTransfer    = (data: any) => api.post('/transfers', data)
export const approveTransfer   = (id: number) => api.patch(`/transfers/${id}/approve`)
export const rejectTransfer    = (id: number, data: any) => api.patch(`/transfers/${id}/reject`, data)
export const cancelTransfer    = (id: number) => api.patch(`/transfers/${id}/cancel`)

// ── COMSET & Asset Checking ────────────────────────────────────────────────
export const getSoftwareLibrary   = ()           => api.get('/sw-library')
export const createSoftware       = (d: any)     => api.post('/sw-library', d)
export const updateSoftware       = (id: number, d: any) => api.put(`/sw-library/${id}`, d)
export const deleteSoftware       = (id: number) => api.delete(`/sw-library/${id}`)

export const getChecklistItems    = ()           => api.get('/checklist-items')
export const createChecklistItem  = (d: any)     => api.post('/checklist-items', d)
export const updateChecklistItem  = (id: number, d: any) => api.put(`/checklist-items/${id}`, d)
export const deleteChecklistItem  = (id: number) => api.delete(`/checklist-items/${id}`)

export const getCheckTemplates    = ()           => api.get('/check-templates')
export const createCheckTemplate  = (d: any)     => api.post('/check-templates', d)
export const updateCheckTemplate  = (id: number, d: any) => api.put(`/check-templates/${id}`, d)
export const deleteCheckTemplate  = (id: number) => api.delete(`/check-templates/${id}`)

export const getComsets           = ()           => api.get('/comsets')
export const getComsetDetail      = (id: number) => api.get(`/comsets/${id}`)
export const createComset         = (d: any)     => api.post('/comsets', d)
export const updateComset         = (id: number, d: any) => api.put(`/comsets/${id}`, d)
export const deleteComset         = (id: number) => api.delete(`/comsets/${id}`)

export const getCheckSessions     = ()           => api.get('/check-sessions')
export const getSessionsByComset  = (id: number) => api.get(`/check-sessions/comset/${id}`)
export const getCheckSession      = (id: number) => api.get(`/check-sessions/${id}`)
export const submitCheckSession   = (d: any)     => api.post('/check-sessions', d)
export const saveCheckProgress    = (dto: any, existingId?: number) => api.post('/check-sessions/progress', { dto, existingId })
export const completeCheckSession = (id: number, dto: any) => api.patch(`/check-sessions/${id}/complete`, dto)
export const cancelCheckSession   = (id: number, reason?: string) =>
  api.delete(`/check-sessions/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`)

// Contracts
export const getContractSummary      = ()            => api.get('/contracts/summary')
export const getContracts            = ()            => api.get('/contracts')
export const getContract             = (id: number)  => api.get(`/contracts/${id}`)
export const createContract          = (dto: any)    => api.post('/contracts', dto)
export const updateContract          = (id: number, dto: any) => api.put(`/contracts/${id}`, dto)
export const deleteContract          = (id: number)  => api.delete(`/contracts/${id}`)
export const getContractCostByVendor = (year?: number) => api.get('/contracts/cost-by-vendor', { params: year ? { year } : {} })
export const addContractPayment      = (contractId: number, dto: any) => api.post(`/contracts/${contractId}/payments`, dto)
export const updateContractPayment   = (payId: number, dto: any)  => api.put(`/contracts/payments/${payId}`, dto)
export const deleteContractPayment   = (payId: number)            => api.delete(`/contracts/payments/${payId}`)
export const uploadContractAttachment = (id: number, file: File) => {
  const form = new FormData(); form.append('file', file)
  return api.post(`/contracts/${id}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
}
export const deleteContractAttachment = (id: number, url: string) => api.delete(`/contracts/${id}/attachments`, { data: { url } })

// Change Log
export const getChangelog = (params: Record<string, any> = {}) =>
  api.get('/changelog', { params })
export const getChangelogStats = (days = 30) =>
  api.get('/changelog/stats', { params: { days } })
export const getChangelogTypes = () => api.get('/changelog/types')

// ── Company Profile (self-service, scoped to caller's own company) ────────────
export const getCompanyProfile = () => api.get('/company/profile')
export const getMyFeatures     = () => api.get('/company/features')
export const updateCompanyProfile = (dto: any) => api.patch('/company/profile', dto)
export const uploadCompanyLogo = (file: File) => {
  const form = new FormData(); form.append('file', file)
  return api.post('/company/profile/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// (Platform / Super-Admin: manage all tenants — ตัดออกแล้วพร้อมหน้า Super Admin panel)

// ── Work Orders ─────────────────────────────────────────────────────────────
export const getWorkOrders   = (status?: string) => api.get('/work-orders', { params: status ? { status } : {} })
export const getWorkOrder    = (id: number) => api.get(`/work-orders/${id}`)
export const createWorkOrder = (dto: any) => api.post('/work-orders', dto)
export const updateWorkOrder = (id: number, dto: any) => api.patch(`/work-orders/${id}`, dto)
export const setWorkOrderStatus = (id: number, status: string) => api.patch(`/work-orders/${id}/status`, { status })
export const closeWorkOrder  = (id: number, dto: any = {}) => api.patch(`/work-orders/${id}/close`, dto)
export const claimWorkOrder  = (id: number) => api.patch(`/work-orders/${id}/claim`)
export const getWorkOrderUpdates = (id: number) => api.get(`/work-orders/${id}/updates`)
export const addWorkOrderUpdate  = (id: number, note: string) => api.post(`/work-orders/${id}/updates`, { note })
export const cancelWorkOrder     = (id: number, reason: string) => api.patch(`/work-orders/${id}/cancel`, { reason })
export const getWorkOrderMaterials   = (id: number) => api.get(`/work-orders/${id}/materials`)
export const addWorkOrderMaterial    = (id: number, dto: { consumableItemId: number; qty: number; locationId?: number }) => api.post(`/work-orders/${id}/materials`, dto)
export const removeWorkOrderMaterial = (id: number, materialId: number) => api.delete(`/work-orders/${id}/materials/${materialId}`)

// ── LINE group linking (self-service, scoped to caller's own company) ────────
export const getLineGroups        = () => api.get('/company/line/groups')
export const unlinkLineGroup      = (id: number) => api.delete(`/company/line/groups/${id}`)
export const regenerateLineCode   = () => api.post('/company/line/regenerate-code')

// ── HK Orders (ของใช้/สิ่งอำนวยความสะดวก) ──────────────────────────────────
export const getHkOrders   = (status?: string) => api.get('/hk-orders', { params: status ? { status } : {} })
export const getHkOrder    = (id: number) => api.get(`/hk-orders/${id}`)
export const createHkOrder = (dto: any) => api.post('/hk-orders', dto)
export const updateHkOrder = (id: number, dto: any) => api.patch(`/hk-orders/${id}`, dto)
export const setHkOrderStatus = (id: number, status: string) => api.patch(`/hk-orders/${id}/status`, { status })
export const closeHkOrder  = (id: number, dto: any = {}) => api.patch(`/hk-orders/${id}/close`, dto)
export const claimHkOrder  = (id: number) => api.patch(`/hk-orders/${id}/claim`)
export const getHkOrderUpdates = (id: number) => api.get(`/hk-orders/${id}/updates`)
export const addHkOrderUpdate  = (id: number, note: string) => api.post(`/hk-orders/${id}/updates`, { note })
export const cancelHkOrder     = (id: number, reason: string) => api.patch(`/hk-orders/${id}/cancel`, { reason })
export const getHkOrderMaterials   = (id: number) => api.get(`/hk-orders/${id}/materials`)
export const addHkOrderMaterial    = (id: number, dto: { consumableItemId: number; qty: number; locationId?: number }) => api.post(`/hk-orders/${id}/materials`, dto)
export const removeHkOrderMaterial = (id: number, materialId: number) => api.delete(`/hk-orders/${id}/materials/${materialId}`)
export const getPendingHkMaterials = () => api.get('/hk-orders/materials/pending')
export const confirmHkMaterial     = (materialId: number, confirmedQty: number) => api.post(`/hk-orders/materials/${materialId}/confirm`, { confirmedQty })
export const markHkOrderReturned = (id: number) => api.patch(`/hk-orders/${id}/returned`)
